<template>
  <div v-if="artifacts.length" class="kh-artifacts">
    <!-- Panel estilo paso 3 de IAModel -->
    <v-card variant="outlined" rounded="lg" class="panel-card d-flex flex-column w-100">
      <!-- HEADER -->
      <div class="panel-header d-flex align-center justify-space-between">
        <div>
          <div class="text-subtitle-2 font-weight-medium">Artefactos generados</div>
          <div class="text-caption text-medium-emphasis">
            {{ artifacts.length }} archivo(s)
          </div>
        </div>
      </div>

      <v-divider />

      <!-- BODY -->
      <div class="panel-body flex-grow-1">
        <v-card
          v-for="a in artifacts"
          :key="a.index ?? a.filename"
          variant="outlined"
          rounded="xl"
          class="mb-2 case-card case-card--compact"
        >
      <v-card-text class="case-body">
        <div class="d-flex align-start justify-space-between ga-3">
          <div class="flex-grow-1 pr-6">
            <div class="case-title-row">
              <v-icon class="case-ico" size="18" :color="iconoArchivo(a.filename).color">
                {{ iconoArchivo(a.filename).icono }}
              </v-icon>
              <div class="case-title">{{ a.label || a.filename }}</div>
            </div>
            <div class="case-desc text-caption text-medium-emphasis">
              {{ a.filename }}
            </div>
          </div>
        </div>

        <v-divider class="my-2" />

        <div class="d-flex justify-end ga-2">
          <v-tooltip v-if="puedePrevisualizar(a.filename)" text="Previsualizar">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                icon
                size="small"
                variant="outlined"
                @click.stop="previsualizar(a)"
              >
                <v-icon>mdi-eye-outline</v-icon>
              </v-btn>
            </template>
          </v-tooltip>

          <v-tooltip text="Descargar">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                icon
                size="small"
                variant="tonal"
                color="#c1121f"
                @click.stop="descargarArtefacto(a)"
              >
                <v-icon>mdi-download</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </v-card-text>
        </v-card>
      </div>
    </v-card>

    <!-- Dialogo de previsualizacion (PDF / imagenes) -->
    <v-dialog
      v-model="previewDialog"
      max-width="1000"
      @update:model-value="onPreviewClose"
    >
      <v-card rounded="lg">
        <v-toolbar density="comfortable" color="transparent" flat>
          <v-toolbar-title class="text-subtitle-1">
            {{ previewName }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-download"
            variant="text"
            title="Descargar"
            @click="descargarArtefacto(previewArt)"
          ></v-btn>
          <v-btn icon="mdi-close" variant="text" @click="previewDialog = false"></v-btn>
        </v-toolbar>
        <v-divider></v-divider>
        <div style="height: 75vh">
          <div
            v-if="previewLoading"
            class="d-flex align-center justify-center"
            style="height: 100%"
          >
            <v-progress-circular indeterminate color="#c1121f" />
          </div>
          <iframe
            v-else-if="previewKind === 'pdf'"
            :src="previewUrl"
            width="100%"
            height="100%"
            frameborder="0"
          ></iframe>
          <div
            v-else-if="previewKind === 'image'"
            class="d-flex align-center justify-center pa-3"
            style="height: 100%"
          >
            <img
              :src="previewUrl"
              alt="preview"
              style="max-width: 100%; max-height: 100%; object-fit: contain"
            />
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import "@/css/ModelIA/StylesModel.css";
import api from "@/api/axios/axios.js";

export default {
  name: "KnowledgeArtifacts",
  props: {
    artifacts: { type: Array, default: () => [] },
    // Necesario para construir la URL de descarga si el artefacto no trae `url`.
    jobId: { type: [String, Number], default: null },
  },
  data() {
    return {
      previewDialog: false,
      previewLoading: false,
      previewUrl: null,
      previewKind: null, // "pdf" | "image"
      previewName: "",
      previewArt: null,
    };
  },
  methods: {
    urlArtefacto(a) {
      return (
        a.url ||
        `/login/knowledgehub/${this.jobId}/artifacts/${encodeURIComponent(
          a.filename,
        )}/`
      );
    },
    descargarArtefacto(a) {
      if (!a) return;
      window.open(this.urlArtefacto(a), "_blank");
    },
    extension(filename) {
      return (filename || "").split(".").pop().toLowerCase();
    },
    puedePrevisualizar(filename) {
      const ext = this.extension(filename);
      return ["pdf", "png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext);
    },
    iconoArchivo(filename) {
      const ext = this.extension(filename);
      if (ext === "pdf") return { icono: "mdi-file-pdf-box", color: "#c1121f" };
      if (["doc", "docx"].includes(ext)) return { icono: "mdi-file-word-box", color: "#1883FF" };
      if (["xls", "xlsx", "csv"].includes(ext)) return { icono: "mdi-file-excel-box", color: "#008000" };
      if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext))
        return { icono: "mdi-file-image-box", color: "#8e44ad" };
      return { icono: "mdi-file-document-outline", color: "#607d8b" };
    },
    async previsualizar(a) {
      const ext = this.extension(a.filename);
      this.previewArt = a;
      this.previewName = a.label || a.filename;
      this.previewKind = ext === "pdf" ? "pdf" : "image";
      this.previewDialog = true;
      this.previewLoading = true;
      try {
        const resp = await api.get(this.urlArtefacto(a), { responseType: "blob" });
        if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = URL.createObjectURL(resp.data);
      } catch (err) {
        console.error(err);
        this.previewDialog = false;
      } finally {
        this.previewLoading = false;
      }
    },
    onPreviewClose(open) {
      if (!open && this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
    },
  },
  beforeUnmount() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
  },
};
</script>
